import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateDeviceGroupRequest, DeviceGroupDto, DeviceGroupService, DeviceGroupType, InfrastructureDto } from '../../../../api/gen';
import { CommonModule } from '@angular/common';
import { JJamesCardComponent } from 'shared-lib';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-device-groups-parking-space',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    JJamesCardComponent,
    MatSnackBarModule,
    MatIconModule
  ],
  templateUrl: './create-device-groups-parking-space.component.html',
  styleUrl: './create-device-groups-parking-space.component.scss'
})
export class CreateDeviceGroupsParkingSpaceComponent {
  @Input({ required: true }) infrastructure!: InfrastructureDto;
  @Output() deviceGroupsCreated = new EventEmitter<DeviceGroupDto[]>();
  @Output() skip = new EventEmitter<void>();

  form: FormGroup;
  isSaving = signal(false);
  deviceGroupTypes = Object.values(DeviceGroupType);

  constructor(
    private fb: FormBuilder,
    private deviceGroupService: DeviceGroupService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      deviceGroups: this.fb.array([])
    });
    this.addDeviceGroup(); // Start with one device group form
  }

  get deviceGroups(): FormArray {
    return this.form.get('deviceGroups') as FormArray;
  }

  newDeviceGroup(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      description: [''],
      type: [DeviceGroupType.Gateway, Validators.required]
    });
  }

  addDeviceGroup() {
    this.deviceGroups.push(this.newDeviceGroup());
  }

  removeDeviceGroup(index: number) {
    this.deviceGroups.removeAt(index);
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const createdDeviceGroups: DeviceGroupDto[] = [];
    const deviceGroupForms = this.deviceGroups.value;

    try {
      for (const group of deviceGroupForms) {
        const request: CreateDeviceGroupRequest = {
          groupName: group.name,
          groupDescription: group.description,
          groupType: group.type,
          infrastructureId: this.infrastructure.id!
        };
        const newDeviceGroup = await firstValueFrom(this.deviceGroupService.deviceGroupsPost(request as any));
        createdDeviceGroups.push(newDeviceGroup as DeviceGroupDto);
      }

      this.snackBar.open(`${createdDeviceGroups.length} device group(s) created successfully.`, 'Close', { duration: 3000 });
      this.deviceGroupsCreated.emit(createdDeviceGroups);

    } catch (error) {
      console.error('Error creating device groups:', error);
      this.snackBar.open('Failed to create one or more device groups. Please try again.', 'Close', { duration: 5000 });
    } finally {
      this.isSaving.set(false);
    }
  }

  onSkip() {
    this.skip.emit();
  }
}
